import { onboardingTasks } from '@ourbranch/lookups';

/**
 * Get document name for a task
 * @param {string} taskName - The task name
 * @param {Object} entityData - Entity data (offer or policy)
 * @param {string} entityData.policyType - The policy type
 * @param {string} entityData.policyId - The policy ID (for policies)
 * @param {string} entityData.offerId - The offer ID (for offers)
 * @returns {string|null} - The document name or null
 */
export const getDocName = (taskName, policyData) => {
  const { policyType, policyId } = policyData;
  const now = new Date();
  switch (taskName) {
    case onboardingTasks.SignApplication:
      return `${now.getFullYear()}-${
        now.getMonth() + 1
      }-${now.getDate()}-${now.getHours()}-${now.getMinutes()}-${policyType[0].toUpperCase()}_APPL`;
    case onboardingTasks.CompleteInventory:
      return `inventory_${policyId}`;
    case onboardingTasks.ConnectedHomeRenewal:
      return `connected_home_renewal_${policyId}`;
    case onboardingTasks.ConfirmWindstormMitigationStatus:
      return `proof_of_advanced_windstorm_mitigation_${policyId}`;
    case onboardingTasks.ProofOfMIPipOption4:
    case onboardingTasks.ProofOfMIPipOption5:
    case onboardingTasks.ProofOfMIPipOption6:
      return `proof_of_mi_pip_limit_eligibility_${policyId}`;
    default:
      return null;
  }
};

/**
 * Get display name for a task
 * @param {string} taskName - The task name
 * @returns {string} - The display name
 */
export const getTaskDisplayName = (taskName) => {
  switch (taskName) {
    case onboardingTasks.NeedProofThatAccidentPreventionCourseTaken:
      return 'Proof of accident prevention certification';
    case onboardingTasks.NeedProofOfPriorCoverage:
      return 'Proof of prior auto insurance coverage';
    case onboardingTasks.ConfirmWindstormMitigationStatus:
      return 'Proof of advanced windstorm mitigation';
    case onboardingTasks.SignApplication:
      return 'Sign policy documents';
    case onboardingTasks.ConnectedHomeRenewal:
      return 'Proof of Connected Home for renewal';
    case onboardingTasks.ProvideProofOfConnectedHome:
      return 'Proof of Connected Home';
    case onboardingTasks.ProofOfMIPipOption4:
    case onboardingTasks.ProofOfMIPipOption5:
    case onboardingTasks.ProofOfMIPipOption6:
      return 'Provide proof of MI PIP medical limit eligibility';
    case onboardingTasks.ProvideProofOfFortifiedHome:
      return 'Proof of Fortified Home certification';
    default:
      return taskName;
  }
};

/**
 * Appcues modal IDs by task name
 */
export const appcuesModalIdsByTaskName = {
  [onboardingTasks.SignApplication]: '63249237-5e04-4437-a496-12ff76cd2648',
  [onboardingTasks.NeedProofOfPriorCoverage]: '5251e741-2a75-4e50-b245-5a1cef831dae',
  [onboardingTasks.ProvideProofOfConnectedHome]: '469fd3b0-415f-4b87-b355-8e9998f6a0ae',
  [onboardingTasks.ConnectedHomeRenewal]: '8417a4bd-f165-48c3-a01c-a845f2ff0029',
  [onboardingTasks.ProvideProofOfGoodStudent]: '3e9f15c2-1e9d-46d7-a0cd-bbbbfb300a0b',
  [onboardingTasks.NeedProofThatAccidentPreventionCourseTaken]: 'b3047d65-9347-4a65-bd77-70deb638bb1b',
  [onboardingTasks.ConfirmWindstormMitigationStatus]: '416d477c-d154-4e60-bd07-ead1f62a4582',
  [onboardingTasks.CompleteInventory]: '473c8d96-e71d-4ea5-83a6-ccf5078c96ea',
  [onboardingTasks.NeedScheduledPersonalPropertyImages]: '9570ff2c-f404-479f-b11c-72373cc01eb5',
  [onboardingTasks.ReviewPotentialUndisclosedDriver]: 'f5065e3e-ca48-41d4-82b1-1d900b0f2b37',
  [onboardingTasks.ProvideProofOfFortifiedHome]: 'a96c262f-26bf-489f-9ab3-2571cb7b7374',
  [onboardingTasks.ProofOfMIPipOption4]: '72c3bd49-ad5a-4721-9482-d1b88967e460',
  [onboardingTasks.ProofOfMIPipOption5]: '0f649d0a-7c88-4f4f-8896-e750a041236d',
  [onboardingTasks.ProofOfMIPipOption6]: '8c926e7b-771d-429f-b530-93d03dc639e9'
};

/**
 * Tasks that require processing/review
 */
export const processingReviewTasks = [
  onboardingTasks.NeedProofOfPriorCoverage,
  onboardingTasks.NeedProofThatAccidentPreventionCourseTaken,
  onboardingTasks.ConfirmWindstormMitigationStatus,
  onboardingTasks.ProvideProofOfConnectedHome,
  onboardingTasks.ConnectedHomeRenewal,
  onboardingTasks.ProofOfMIPipOption4,
  onboardingTasks.ProofOfMIPipOption5,
  onboardingTasks.ProofOfMIPipOption6,
  onboardingTasks.ProvideProofOfGoodStudent,
  onboardingTasks.SignApplication,
  onboardingTasks.ProvideProofOfFortifiedHome
];

/**
 * Tasks that can be uploaded by external agents
 */
export const tasksThatCanBeUploadedByExternalAgent = [
  onboardingTasks.NeedProofOfPriorCoverage,
  onboardingTasks.ProvideProofOfConnectedHome,
  onboardingTasks.ConnectedHomeRenewal,
  onboardingTasks.NeedScheduledPersonalPropertyImages,
  onboardingTasks.ConfirmWindstormMitigationStatus,
  onboardingTasks.NeedProofThatAccidentPreventionCourseTaken,
  onboardingTasks.ProvideProofOfGoodStudent
];
